import { Coins } from "lucide-react";
import { useCreditUsage } from "@/hooks/useCreditUsage";
import { formatCredits } from "@/lib/format-credits";
import { cn } from "@/lib/utils";

interface Props {
  onClick?: () => void;
  className?: string;
}

/**
 * Header credits chip — balance + last-30d spend.
 * Used by both the desktop StitchDesktopHeader and mobile MobileShell header,
 * so this single chip is how "credit usage" surfaces across the whole app.
 */
export const CreditsChip = ({ onClick, className }: Props) => {
  const { balance, spent30d, lifetimeSpent, loading } = useCreditUsage();
  const low = balance !== null && balance <= 2;

  const tooltip = `${formatCredits(balance)} credits left · ${formatCredits(spent30d)} used in last 30 days · ${formatCredits(lifetimeSpent)} lifetime`;

  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-semibold transition-all",
        low
          ? "border-warning/40 bg-warning/10 text-warning hover:bg-warning/20"
          : "border-border bg-card text-foreground hover:border-primary/40",
        className,
      )}
      aria-label={tooltip}
      title={tooltip}
    >
      <Coins className="h-3.5 w-3.5" />
      <span className="tabular-nums">{loading ? "—" : formatCredits(balance)}</span>
      <span className="hidden sm:inline text-muted-foreground font-normal">left</span>
      {!loading && spent30d > 0 && (
        <span className="hidden md:inline text-[10px] font-medium text-muted-foreground/80 tabular-nums">
          · {formatCredits(spent30d)} used 30d
        </span>
      )}
    </button>
  );
};
